import { describeError } from '../../lib/errors.js';
import { useCallback, useRef, useState } from 'react';
import {
  confirmUpload,
  requestUpload,
  uploadToStorage,
} from '../../lib/api/endpoints.js';
import { prepareAudio, type PreparedAudio } from '../../lib/audio/prepare.js';
import { assessTake } from '../../lib/audio/assess.js';

export type UploadPhase =
  | { status: 'idle' }
  | { status: 'reading' }
  /** Nothing usable on the take. Refused here, before any bytes are sent. */
  | { status: 'unusable'; message: string }
  | { status: 'uploading'; progress: number }
  | { status: 'done'; assetId: string }
  | { status: 'failed'; message: string };

/**
 * Uploads a recording **while the contributor is still answering questions.**
 *
 * This is the single mechanic that makes a sub-45-second contribution possible.
 * The alternative — fill in the form, press submit, then watch a progress bar —
 * adds the entire upload duration to perceived time, and on a phone with a
 * minute of FLAC that is most of the interaction.
 *
 * So: the moment a file is chosen, hash it, decode it, request a presigned URL
 * and start pushing bytes. By the time the three questions are answered the
 * upload has almost always finished, and submit is instant.
 *
 * The trade is orphaned uploads when someone abandons the form. That is a real
 * cost, and it is why the API sweeps unattached media — a cheaper problem than
 * a contribution flow people give up on.
 */
export function useUpload() {
  const [phase, setPhase] = useState<UploadPhase>({ status: 'idle' });
  const [audio, setAudio] = useState<PreparedAudio | null>(null);

  // Guards against a stale upload finishing after the user has picked a
  // different file and overwriting the newer result.
  const generation = useRef(0);

  const start = useCallback(async (file: File) => {
    const mine = ++generation.current;
    const isStale = () => generation.current !== mine;

    setAudio(null);
    setPhase({ status: 'reading' });

    let prepared: PreparedAudio;
    try {
      prepared = await prepareAudio(file);
    } catch (error) {
      if (isStale()) return;
      setPhase({
        status: 'failed',
        message: describeError(error).message,
      });
      return;
    }

    if (isStale()) return;

    // Silence and mis-taps stop here: an empty take costs the contributor an
    // upload, the corpus a row, and a reviewer a look, for audio that could
    // never have been used. `audio` stays null so the A/B/C questions never
    // open on a recording there is nothing to ask about. A merely *poor* take
    // is not refused — that warning is presentation, and lives in the page.
    const assessment = assessTake(prepared);
    if (assessment.severity === 'block') {
      setPhase({ status: 'unusable', message: assessment.message ?? 'That take was unusable.' });
      return;
    }

    setAudio(prepared);
    setPhase({ status: 'uploading', progress: 0 });

    try {
      const presigned = await requestUpload({
        contentType: file.type,
        sizeBytes: file.size,
        checksumSha256: prepared.checksumSha256,
      });

      if (isStale()) return;

      await uploadToStorage(presigned, file, (fraction) => {
        if (!isStale()) setPhase({ status: 'uploading', progress: fraction });
      });

      if (isStale()) return;

      // Confirm tells the server to verify the bytes that actually landed
      // against the checksum we declared. Idempotent, so a retry after a
      // dropped connection is safe.
      await confirmUpload(presigned.assetId, prepared.durationMs);

      if (isStale()) return;
      setPhase({ status: 'done', assetId: presigned.assetId });
    } catch (error) {
      if (isStale()) return;
      setPhase({
        status: 'failed',
        message: describeError(error).message,
      });
    }
  }, []);

  const reset = useCallback(() => {
    generation.current++;
    setAudio(null);
    setPhase({ status: 'idle' });
  }, []);

  /** Re-runs the upload for the file already prepared, after a failure. */
  const retry = useCallback(() => {
    if (audio) void start(audio.file);
  }, [audio, start]);

  return { phase, audio, start, retry, reset };
}
